// Espace admin AERH — gestion des abonnements clients.
// Réservé au compte administrateur (role === "admin"). Permet de consulter les
// abonnements, résilier / réactiver et changer la formule sans passer par Stripe.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Search, LogOut, RefreshCw, Users } from "lucide-react";

const LOGO = "/logo_GAP.jpg";

const STATUT_STYLE = {
  trial: "bg-[#E8F4F4] text-[#1D3D3D] border-[#4A8A8A]/40",
  actif: "bg-emerald-50 text-emerald-700 border-emerald-200",
  en_attente_paiement: "bg-amber-50 text-amber-700 border-amber-200",
  résilié: "bg-orange-50 text-orange-700 border-orange-200",
  expiré: "bg-rose-50 text-rose-700 border-rose-200",
};
const STATUT_LABEL = {
  trial: "Essai",
  actif: "Actif",
  en_attente_paiement: "Paiement en attente",
  résilié: "Résilié",
  expiré: "Expiré",
};
const FORMULES = [
  { code: "solo", libelle: "Solo" },
  { code: "equipe", libelle: "Équipe" },
  { code: "structure", libelle: "Structure" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
};

function Badge({ statut }) {
  return (
    <span
      data-testid={`statut-badge-${statut}`}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
        STATUT_STYLE[statut] || "bg-gray-50 text-gray-600 border-gray-200"
      }`}
    >
      {STATUT_LABEL[statut] || statut}
    </span>
  );
}

export default function AdminAbonnements() {
  const navigate = useNavigate();
  const { user, estAdmin, logout } = useAuth();
  const [data, setData] = useState(null);
  const [recherche, setRecherche] = useState("");
  const [chargement, setChargement] = useState(true);
  const [actionEnCours, setActionEnCours] = useState(null);
  const [nouvelleFormule, setNouvelleFormule] = useState({});

  // Garde : seul l'admin accède à cet écran.
  useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
    else if (user && typeof user === "object" && !estAdmin) navigate("/tableau-de-bord", { replace: true });
  }, [user, estAdmin, navigate]);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/admin/abonnements");
      setData(data);
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    if (estAdmin) charger();
  }, [estAdmin, charger]);

  const lignes = useMemo(() => {
    if (!data) return [];
    const q = recherche.trim().toLowerCase();
    if (!q) return data.abonnements;
    return data.abonnements.filter((a) =>
      `${a.raison_sociale} ${a.email}`.toLowerCase().includes(q),
    );
  }, [data, recherche]);

  const executer = async (id, action, payload) => {
    setActionEnCours(`${id}:${action}`);
    try {
      await api.post(`/admin/abonnements/${id}/${action}`, payload || {});
      toast.success("Action effectuée.");
      await charger();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail));
    } finally {
      setActionEnCours(null);
    }
  };

  if (!estAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#E8F4F4]">
        <Loader2 className="h-7 w-7 animate-spin text-[#4A8A8A]" />
      </div>
    );
  }

  return (
    <div data-testid="page-admin-abonnements" className="min-h-screen bg-[#E8F4F4]">
      {/* En-tête admin */}
      <header className="bg-[#1D3D3D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/admin/abonnements" className="flex items-center gap-3">
            <img src={LOGO} alt="Groupe AERH Polynésie" className="h-8 w-auto rounded bg-white p-0.5" />
            <div className="leading-tight">
              <p className="text-sm font-extrabold tracking-tight">Porinetia Compta AERH</p>
              <p className="text-[11px] text-[#4A8A8A]">Espace administrateur</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-white/70">{user?.email}</span>
            <Button
              data-testid="admin-logout-btn"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              variant="outline"
              className="h-9 bg-transparent border-white/30 text-white hover:bg-white/10"
            >
              <LogOut className="h-4 w-4 mr-1" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-[#1D3D3D] flex items-center gap-2">
              <Users className="h-6 w-6 text-[#4A8A8A]" /> Abonnements clients
            </h1>
            <p className="mt-1 text-sm text-[#1D3D3D]/70">
              Gérez les abonnements sans passer par le tableau de bord Stripe.
            </p>
          </div>
          <Button
            data-testid="admin-refresh-btn"
            onClick={charger}
            variant="outline"
            className="h-9 border-[#4A8A8A]/40 text-[#1D3D3D] bg-white"
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
          </Button>
        </div>

        {/* Statistiques */}
        {data?.stats && (
          <div data-testid="admin-stats" className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-white rounded-xl p-3 border border-[#4A8A8A]/20">
              <p className="text-xs font-semibold text-[#1D3D3D]/60 uppercase tracking-wide">Total</p>
              <p className="text-2xl font-black text-[#1D3D3D]">{data.total}</p>
            </div>
            {Object.entries(data.stats).map(([statut, n]) => (
              <div key={statut} className="bg-white rounded-xl p-3 border border-[#4A8A8A]/20">
                <p className="text-xs font-semibold text-[#1D3D3D]/60 uppercase tracking-wide">
                  {STATUT_LABEL[statut] || statut}
                </p>
                <p className="text-2xl font-black text-[#1D3D3D]">{n}</p>
              </div>
            ))}
          </div>
        )}

        {/* Recherche */}
        <div className="mt-6 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A8A8A]" />
          <Input
            data-testid="admin-search"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher par entreprise ou email…"
            className="pl-9 bg-white border-[#4A8A8A]/30 h-10"
          />
        </div>

        {/* Tableau */}
        <div className="mt-4 bg-white rounded-xl border border-[#4A8A8A]/20 overflow-hidden">
          {chargement ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="h-7 w-7 animate-spin text-[#4A8A8A]" />
            </div>
          ) : lignes.length === 0 ? (
            <p data-testid="admin-aucun" className="py-16 text-center text-sm text-[#1D3D3D]/60">
              Aucun abonnement trouvé.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#E8F4F4] text-[#1D3D3D] text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Entreprise</th>
                    <th className="text-left font-semibold px-4 py-3">Formule</th>
                    <th className="text-left font-semibold px-4 py-3">Statut</th>
                    <th className="text-left font-semibold px-4 py-3">Fin d'essai</th>
                    <th className="text-left font-semibold px-4 py-3">Prochain paiement</th>
                    <th className="text-right font-semibold px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8F4F4]">
                  {lignes.map((a) => (
                    <tr key={a.id} data-testid={`admin-ligne-${a.id}`} className="hover:bg-[#E8F4F4]/40">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-[#1D3D3D]">{a.raison_sociale}</p>
                        <p className="text-xs text-[#1D3D3D]/55">{a.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-[#1D3D3D]">{a.formule_libelle}</span>
                        <span className="text-xs text-[#1D3D3D]/55"> · {a.periodicite}</span>
                      </td>
                      <td className="px-4 py-3"><Badge statut={a.statut} /></td>
                      <td className="px-4 py-3 text-[#1D3D3D]/80">{fmtDate(a.date_fin_trial)}</td>
                      <td className="px-4 py-3 text-[#1D3D3D]/80">{fmtDate(a.date_prochain_paiement)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          {/* Changer de formule */}
                          <div className="flex items-center gap-1">
                            <Select
                              value={nouvelleFormule[a.id] || ""}
                              onValueChange={(v) => setNouvelleFormule((s) => ({ ...s, [a.id]: v }))}
                            >
                              <SelectTrigger
                                data-testid={`admin-select-formule-${a.id}`}
                                className="h-8 w-[120px] bg-white border-[#4A8A8A]/30 text-xs"
                              >
                                <SelectValue placeholder="Formule…" />
                              </SelectTrigger>
                              <SelectContent>
                                {FORMULES.map((f) => (
                                  <SelectItem key={f.code} value={f.code}>{f.libelle}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              data-testid={`admin-changer-${a.id}`}
                              size="sm"
                              variant="outline"
                              disabled={!nouvelleFormule[a.id] || actionEnCours === `${a.id}:changer-formule`}
                              onClick={() =>
                                executer(a.id, "changer-formule", {
                                  formule: nouvelleFormule[a.id],
                                  periodicite: a.periodicite || "mensuel",
                                })
                              }
                              className="h-8 border-[#4A8A8A]/40 text-[#1D3D3D]"
                            >
                              {actionEnCours === `${a.id}:changer-formule` ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                "Appliquer"
                              )}
                            </Button>
                          </div>

                          {/* Résilier / Réactiver */}
                          {a.statut === "résilié" ? (
                            <Button
                              data-testid={`admin-reactiver-${a.id}`}
                              size="sm"
                              disabled={actionEnCours === `${a.id}:reactiver`}
                              onClick={() => executer(a.id, "reactiver")}
                              className="h-8 bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white"
                            >
                              Réactiver
                            </Button>
                          ) : (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  data-testid={`admin-resilier-${a.id}`}
                                  size="sm"
                                  variant="ghost"
                                  disabled={actionEnCours === `${a.id}:resilier`}
                                  className="h-8 text-[#8A4A4A] hover:bg-[#8A4A4A]/10"
                                >
                                  Résilier
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent data-testid={`admin-dialog-resilier-${a.id}`}>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Résilier l'abonnement de {a.raison_sociale} ?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    L'abonnement sera résilié en fin de période payée. Le client conserve l'accès
                                    jusqu'à cette date. Vous pourrez réactiver avant l'échéance.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction
                                    data-testid={`admin-confirmer-resilier-${a.id}`}
                                    onClick={() => executer(a.id, "resilier")}
                                    className="bg-[#8A4A4A] hover:bg-[#6e3b3b] text-white"
                                  >
                                    Confirmer la résiliation
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
